import html2pdf from 'html2pdf.js';
import htmlDocx from 'html-docx-js/dist/html-docx';

export interface PdfExportOptions {
  filename: string;
  margin?: number;
  pagebreakBefore?: string[];
  pagebreakAvoid?: string[];
  renderDelayMs?: number;
}

const DEFAULT_PDF_PAGE_BREAK_AVOID = [
  '.question',
  '.answer-choices',
  '.answer-choice',
  'h1',
  'h2',
  'h3',
  'p',
];

/**
 * Export a DOM element to PDF via html2pdf.js
 */
export async function exportElementToPdf(
  element: HTMLElement,
  options: PdfExportOptions,
): Promise<void> {
  const {
    filename,
    margin = 0.7,
    pagebreakBefore = [],
    pagebreakAvoid = DEFAULT_PDF_PAGE_BREAK_AVOID,
    renderDelayMs = 100,
  } = options;

  if (renderDelayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, renderDelayMs));
  }

  const pdfOptions = {
    margin,
    filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
      letterRendering: true,
      windowWidth: 1200,
      scrollY: -window.scrollY,
      allowTaint: true,
    },
    jsPDF: {
      unit: 'in',
      format: 'letter',
      orientation: 'portrait',
      compress: true,
      hotfixes: ['px_scaling'],
      precision: 16,
    },
    pagebreak: {
      mode: ['css', 'legacy', 'avoid-all'],
      before: pagebreakBefore,
      avoid: pagebreakAvoid,
    },
  };

  await html2pdf()
    .set(pdfOptions as Parameters<ReturnType<typeof html2pdf>['set']>[0])
    .from(element)
    .save();
}

/** Download a blob with a temporary anchor element */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/** Open a new window and trigger the browser print dialog */
export function printHtmlDocument(
  bodyHtml: string,
  options: { title?: string; styles?: string } = {},
): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const { title = 'Document', styles = '' } = options;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>${styles}</style>
      </head>
      <body>
        ${bodyHtml}
        <script>
          window.onload = function() { window.print(); };
        </script>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
}

/** Word download via Blob (MS Word HTML MIME) */
export function downloadWordHtmlBlob(fullHtml: string, filename: string): void {
  const blob = new Blob([fullHtml], {
    type: 'application/vnd.ms-word;charset=utf-8',
  });
  downloadBlob(blob, filename.endsWith('.doc') ? filename : `${filename}.doc`);
}

/** Word download via html-docx-js */
export function exportHtmlToDocx(fullHtml: string, filename: string): void {
  const docxBlob = htmlDocx.asBlob(fullHtml);
  downloadBlob(docxBlob, filename.endsWith('.docx') ? filename : `${filename}.docx`);
}

/** Sanitize a string for use in download filenames */
export function sanitizeFilename(title: string, fallback = 'document'): string {
  const cleaned = title.replace(/^#+\s*/, '').trim();
  const safe = cleaned.replace(/[^a-z0-9\-\s_]/gi, '').replace(/\s+/g, '_').toLowerCase();
  return safe || fallback;
}
